<template>
  <BaseModal
    v-if="activeAddOn"
    is-open
    close-on-esc-key-click
    class-name="addon-details-modal"
    wrapper-class-name="addon-details-modal-wrapper"
    @close="close"
  >
    <div class="flex flex-col gap-4">
      <!-- Photo: a landscape one fits its box, a portrait one fills a square; omitted entirely when there's none -->
      <AddOnModalPhoto
        v-if="activeAddOn.modalImageUrl"
        :src="activeAddOn.modalImageUrl"
        :alt="activeAddOn.name"
        :width="activeAddOn.modalImageWidth"
        :height="activeAddOn.modalImageHeight"
      />

      <div class="flex flex-col gap-2">
        <span class="text-sm text-grey-800 wrap-anywhere">{{ activeAddOn.categorySubcategoryLabel }}</span>
        <div class="flex items-start justify-between gap-4">
          <h3 class="m-0 text-lg font-semibold wrap-anywhere">
            {{ activeAddOn.name }}
          </h3>
          <div class="shrink-0 text-sm text-right whitespace-nowrap">
            <span class="mr-1 font-semibold">{{ activeAddOn.price.formatted }}</span>
            <span v-if="activeAddOn.pricingLabel">{{ activeAddOn.pricingLabel }}</span>
          </div>
        </div>

        <p
          v-if="activeAddOn.description"
          class="m-0 text-sm whitespace-pre-line wrap-anywhere"
        >
          {{ activeAddOn.description }}
        </p>
      </div>
    </div>

    <template #actions>
      <div class="w-full flex justify-end">
        <BaseButton color="white" class="w-full sm:w-auto" @click="close">
          Close
        </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

import AddOnModalPhoto from '@/components/AddOns/AddOnModalPhoto.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import { RoomAddOn } from '@/modules/public/enquiry-form/enquiry-form.types';

const activeAddOn = ref<RoomAddOn | null>(null);

const findAddOn = (addOnId: number): RoomAddOn | null => {
  const addOns = window.__TGV_ROOM_ADDONS ?? [];
  return addOns.find((addOn) => addOn.id === addOnId) ?? null;
};

const open = (addOnId: number): void => {
  const addOn = findAddOn(addOnId);
  if (!addOn) return;
  activeAddOn.value = addOn;
};

const close = (): void => {
  activeAddOn.value = null;
};

onMounted(() => {
  window.triggerShowRoomAddOnModal = open;
});

onBeforeUnmount(() => {
  delete window.triggerShowRoomAddOnModal;
});
</script>

<style scoped lang="scss">
@use "@sass/base/variables";

/* The design-system sheet is bottom-anchored on mobile via justify-content: flex-end, which clips
   anything taller than the viewport above the top edge, where the wrapper cannot scroll. An auto
   top margin keeps a short sheet at the bottom and lets a tall one start at the top and scroll. */
:deep(.ds-modal-wrapper.addon-details-modal-wrapper) {
  @media #{variables.$for-mobile} {
    justify-content: flex-start;

    > .ds-modal {
      margin-top: auto;
    }
  }
}

/* Close button sits 16px from the top-right corner (design spec), overriding the default -6px/-10px inset. */
:deep(.addon-details-modal .ds-modal__close_button) {
  margin: -24px -24px 0 0;

  @media #{variables.$for-mobile} {
    margin: -8px -8px 0 0;
  }
}
</style>
